import React, { useRef, useEffect, useState } from 'react';
import { Button, Box, Typography, CircularProgress, Tooltip, IconButton } from '@mui/material';
import { Download, FileText, HelpCircle, Printer } from 'lucide-react';
import Chart from 'chart.js/auto';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { REPORT_STYLES, getChartColors, ensureChartData } from './reportStyles';

/**
 * Sales Report Generator Component
 * Creates a downloadable HTML/PDF report from sales analysis data
 */
const SalesReportGenerator = ({ 
  salesAnalysisMessage, 
  carInsightsData,
  companyData,
  loading = false,
  onGenerateReport
}) => {
  const reportRef = useRef(null);
  const chartRefs = useRef({});
  const chartInstances = useRef({});
  const [exportType, setExportType] = useState('html');
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [metricsData, setMetricsData] = useState({
    sold: 0,
    total: 0,
    remaining: 0,
    brands: [],
    models: [],
    bodyTypes: []
  });

  // Parse metrics from sales analysis text if not already in carInsightsData
  useEffect(() => {
    if (salesAnalysisMessage && (!carInsightsData?.data || 
        carInsightsData.data.sold === 0)) {
      
      // Extract metrics using regex patterns
      const extractMetrics = () => {
        const salesText = salesAnalysisMessage;
        
        // Auto vendute pattern
        const soldPattern = /Auto vendute:\s*(\d+)\s*su\s*(\d+)/i;
        const soldMatch = salesText.match(soldPattern);
        
        // Remaining stock pattern
        const remainingPattern = /Auto rimaste in stock[^:]*:\s*(\d+)/i;
        const remainingMatch = salesText.match(remainingPattern);
        
        // Extract brand data
        const brandPattern = /([A-Za-z]+):\s*(\d+)\s*auto/gi;
        const brandMatches = [...salesText.matchAll(brandPattern)];
        const brands = brandMatches.map(match => ({ 
          brand: match[1], 
          count: parseInt(match[2]) 
        }));
        
        // Extract model data
        const modelPattern = /([\w-]+)\s*\((\d+)\)/g;
        const modelSection = salesText.split('Modelli top:')[1]?.split('\n')[0];
        const modelMatches = modelSection ? [...modelSection.matchAll(modelPattern)] : [];
        const models = modelMatches.map(match => ({ 
          model: match[1].trim(), 
          count: parseInt(match[2]) 
        }));
        
        // Extract body types
        const bodyPattern = /(SUV|Berlina|berlina|City Car|city car|Wagon|wagon|coupé|Coupé)(?:\s*\((\d+)[^)]*\))?/gi;
        const bodySection = salesText.includes('Carrozzerie più richieste:') 
          ? salesText.split('Carrozzerie più richieste:')[1].split('\n')[0]
          : '';
        const bodyMatches = [...bodySection.matchAll(bodyPattern)];
        const bodyTypes = bodyMatches.map(match => ({ 
          type: match[1], 
          count: match[2] ? parseInt(match[2]) : 0
        }));
        
        setMetricsData({
          sold: soldMatch ? parseInt(soldMatch[1]) : 0,
          total: soldMatch ? parseInt(soldMatch[2]) : 0,
          remaining: remainingMatch ? parseInt(remainingMatch[1]) : 0,
          brands,
          models,
          bodyTypes
        });
      };
      
      extractMetrics();
    } else if (carInsightsData?.data) {
      setMetricsData(carInsightsData.data);
    }
  }, [salesAnalysisMessage, carInsightsData]);

  // Generate charts from insights data
  useEffect(() => {
    if ((carInsightsData || metricsData) && reportRef.current) {
      // Clear any existing charts
      Object.values(chartInstances.current).forEach(chart => {
        if (chart) chart.destroy();
      });
      chartInstances.current = {};

      // Use local metrics data if carInsightsData is incomplete
      const data = carInsightsData?.chart_data || {};
      const chartsToDraw = [];
      
      // Create distribution chart if needed
      if (data.distribution || metricsData.brands?.length > 0) {
        const distributionData = data.distribution || {
          title: 'Distribuzione Vendite per Marca',
          labels: metricsData.brands.map(b => b.brand),
          data: metricsData.brands.map(b => b.count)
        };
        chartsToDraw.push(['distribution', distributionData, 'pie']);
      }
      
      // Create body styles chart if needed
      if (data.overall_body_styles || metricsData.bodyTypes?.length > 0) {
        const bodyStylesData = data.overall_body_styles || {
          title: 'Vendite per Tipo di Carrozzeria',
          labels: metricsData.bodyTypes.map(b => b.type),
          data: metricsData.bodyTypes.map(b => b.count || 1)
        };
        chartsToDraw.push(['overall_body_styles', bodyStylesData, 'pie']);
      }
      
      // Create sales status chart if needed
      if (data.sales_status || (metricsData.sold > 0 && metricsData.remaining > 0)) {
        const salesStatusData = data.sales_status || {
          title: 'Stato delle Vendite',
          labels: ['Vendute', 'Disponibili'],
          data: [metricsData.sold, metricsData.remaining]
        };
        chartsToDraw.push(['sales_status', salesStatusData, 'doughnut']);
      }
      
      // Draw all charts
      chartsToDraw.forEach(([key, data, type]) => {
        if (chartRefs.current[key]) {
          try {
            // Ensure we have data to display
            const chartData = {
              labels: data.labels && data.labels.length ? data.labels : ['No Data'],
              data: data.data && data.data.length ? data.data : [1],
              title: data.title || key
            };
            
            const ctx = chartRefs.current[key].getContext('2d');
            
            // Get the appropriate color scheme based on chart type
            const colorScheme = getChartColors(key);
            
            chartInstances.current[key] = new Chart(ctx, {
              type: type,
              data: {
                labels: chartData.labels,
                datasets: [{
                  label: chartData.title,
                  data: chartData.data,
                  backgroundColor: colorScheme.backgroundColor,
                  borderColor: colorScheme.borderColor,
                  borderWidth: 1
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  title: {
                    display: true,
                    text: chartData.title,
                    font: {
                      size: 16,
                      weight: 'bold'
                    }
                  },
                  legend: {
                    position: 'bottom',
                    labels: {
                      boxWidth: 12,
                      padding: 15,
                      usePointStyle: true
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.raw || 0;
                        const total = context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0);
                        const percentage = Math.round((value / total) * 100);
                        return `${label}: ${value} (${percentage}%)`;
                      }
                    }
                  }
                }
              }
            });
          } catch (error) {
            console.error(`Error creating chart ${key}:`, error);
          }
        }
      });
    }
  }, [carInsightsData, metricsData]);

  // Generate and download HTML report
  const generateHtmlReport = () => {
    if (!reportRef.current) return;

    // Format date for filename
    const date = new Date();
    const formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD
    
    // Create a new document
    const reportContent = reportRef.current.innerHTML;
    const blob = new Blob([
      `<!DOCTYPE html>
      <html lang="it">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Analisi Vendite Auto - ${formattedDate}</title>
        <style>
          ${REPORT_STYLES}
        </style>
        <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
      </head>
      <body>
        <div class="report-container">
          ${reportContent}
          <div class="footer">
            <p>Report generato il ${date.toLocaleDateString('it-IT')} alle ${date.toLocaleTimeString('it-IT')}</p>
            <p>Analisi Vendite Auto - ${companyData?.name || 'Concessionario Auto'}</p>
          </div>
        </div>
      </body>
      </html>`
    ], { type: 'text/html' });
    
    saveAs(blob, `analisi-vendite-${companyData?.name || 'auto'}-${formattedDate}.html`);
    
    // Call the callback if provided
    if (onGenerateReport) {
      onGenerateReport();
    }
  };

  // Generate PDF report
  const generatePdfReport = async () => {
    if (!reportRef.current) return;
    
    try {
      setGeneratingPdf(true);
      
      // Make sure reportRef is visible during PDF generation
      reportRef.current.style.display = 'block';
      reportRef.current.style.width = '900px';
      reportRef.current.style.height = 'auto';
      reportRef.current.style.position = 'absolute';
      reportRef.current.style.left = '-9999px';
      
      // Wait a bit for charts to render
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Reset display style
      reportRef.current.style.display = 'none';
      reportRef.current.style.position = 'static';
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calculate how many pages we need
      const totalPages = Math.ceil(imgHeight / (pageHeight - 20));
      
      // Add each page
      for (let i = 0; i < totalPages; i++) {
        if (i > 0) pdf.addPage();
        
        const sourceY = i * (pageHeight - 20) * (canvas.height / imgHeight);
        const sourceHeight = Math.min(
          canvas.height - sourceY,
          (pageHeight - 20) * (canvas.height / imgHeight)
        );
        
        pdf.addImage(
          imgData, 
          'JPEG', 
          10, // X position
          10, // Y position
          imgWidth, 
          imgHeight,
          null, // Alias
          'FAST', // Compression
          i === 0 ? 0 : -i * (pageHeight - 20) // Source Y for slicing
        );
      }
      
      // Format date for filename
      const date = new Date();
      const formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD
      pdf.save(`analisi-vendite-${companyData?.name || 'auto'}-${formattedDate}.pdf`);
      
      // Call the callback if provided
      if (onGenerateReport) {
        onGenerateReport();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Handle report generation
  const handleGenerateReport = () => {
    if (exportType === 'html') {
      generateHtmlReport();
    } else if (exportType === 'pdf') {
      generatePdfReport();
    }
  };

  // Extract period from sales message
  const extractPeriod = () => {
    if (!salesAnalysisMessage) return 'Periodo di analisi';
    
    const periodMatch = salesAnalysisMessage.match(/vendite\s+auto\s+(?:di|del)\s+([^\(]+)(?:\s*\(([^\)]+)\))?/i);
    if (periodMatch) {
      const period = periodMatch[1].trim();
      const dates = periodMatch[2] ? ` (${periodMatch[2].trim()})` : '';
      return period + dates;
    }
    
    return carInsightsData?.analysis_metadata?.date_range || 'Periodo di analisi';
  };

  const getPeriod = () => {
    return carInsightsData?.analysis_metadata?.date_range || extractPeriod();
  };

  const handlePrint = () => {
    if (!reportRef.current) return;
    
    // Create a printable version
    const printWindow = window.open('', '_blank');
    const date = new Date();
    
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html lang="it">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>Analisi Vendite Auto - Print</title>
          <style>
            ${REPORT_STYLES}
          </style>
        </head>
        <body>
          <div class="report-container" style="box-shadow: none">
            ${reportRef.current.innerHTML}
            <div class="footer">
              <p>Report generato il ${date.toLocaleDateString('it-IT')} alle ${date.toLocaleTimeString('it-IT')}</p>
              <p>Analisi Vendite Auto - ${companyData?.name || 'Concessionario Auto'}</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 2, 
          flexWrap: 'wrap', 
          alignItems: 'center', 
          mb: 2 
        }}
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={loading || generatingPdf ? 
            <CircularProgress size={20} color="inherit" /> : 
            <FileText size={18} color={exportType === 'pdf' ? '#ff0066' : undefined} />
          }
          onClick={handleGenerateReport}
          disabled={loading || generatingPdf}
          sx={{
            background: 'linear-gradient(135deg, #3a86ff 0%, #ff006e 100%)',
            '&:hover': { background: 'linear-gradient(135deg, #ff006e 0%, #3a86ff 100%)' }
          }}
        >
          {loading ? 'Caricamento...' : 
           generatingPdf ? 'Generazione PDF...' : 
           `Scarica ${exportType.toUpperCase()}`}
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant={exportType === 'html' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setExportType('html')}
            sx={{ 
              minWidth: '40px', 
              mr: 1,
              backgroundColor: exportType === 'html' ? 'primary.main' : 'transparent',
              color: exportType === 'html' ? 'white' : 'primary.main'
            }}
          >
            HTML
          </Button>
          <Button
            variant={exportType === 'pdf' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => setExportType('pdf')}
            sx={{ 
              minWidth: '40px',
              backgroundColor: exportType === 'pdf' ? 'primary.main' : 'transparent',
              color: exportType === 'pdf' ? 'white' : 'primary.main'
            }}
          >
            PDF
          </Button>
        </Box>
        
        <Button
          variant="outlined"
          size="small"
          startIcon={<Printer size={18} />}
          onClick={handlePrint}
          sx={{ ml: 'auto' }}
        >
          Stampa
        </Button>
        
        <Tooltip title="Il report include l'analisi vendite con grafici, statistiche e il logo aziendale. Scegli il formato HTML per un documento interattivo o PDF per la condivisione.">
          <IconButton size="small" color="primary">
            <HelpCircle size={18} />
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Hidden report template for generation */}
      <Box 
        ref={reportRef} 
        sx={{ 
          display: 'none', // Hidden by default
          p: 3, 
          bgcolor: 'white',
          color: 'black',
          width: '100%'
        }}
      >
        {/* Company Header */}
        <Box className="company-header">
          {companyData?.logo_url && (
            <img 
              src={companyData.logo_url} 
              alt={companyData.name || 'Logo Concessionario'} 
              className="company-logo"
              crossOrigin="anonymous"
            />
          )}
          <Box>
            <Typography variant="h3" className="report-title">
              Analisi Vendite Auto
            </Typography>
            <Typography variant="subtitle1" className="report-date">
              {getPeriod()}
            </Typography>
            <Typography variant="h5" className="company-name">
              {companyData?.name || 'Concessionario Auto'}
            </Typography>
          </Box>
        </Box>

        {/* Analysis Text */}
        <div className="section">
          <h2 className="section-title">Riepilogo Analisi</h2>
          <div className="analysis-text">
            {salesAnalysisMessage || 'Nessun dato di analisi disponibile'}
          </div>
        </div>

        {/* Charts */}
        <div className="section">
          <h2 className="section-title">Grafici e Statistiche</h2>
          <div className="charts-container">
            {/* Distribution Chart */}
            <div className="chart-wrapper">
              <div className="chart-title">
                Distribuzione Vendite per Marca
              </div>
              <canvas 
                ref={el => chartRefs.current.distribution = el} 
                width="400" 
                height="250"
              ></canvas>
            </div>
            
            {/* Body Styles Chart */}
            <div className="chart-wrapper">
              <div className="chart-title">
                Vendite per Tipo di Carrozzeria
              </div>
              <canvas 
                ref={el => chartRefs.current.overall_body_styles = el} 
                width="400" 
                height="250"
              ></canvas>
            </div>
            
            {/* Sales Status Chart */}
            <div className="chart-wrapper">
              <div className="chart-title">
                Stato delle Vendite
              </div>
              <canvas 
                ref={el => chartRefs.current.sales_status = el} 
                width="400" 
                height="250"
              ></canvas>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="section">
          <h2 className="section-title">Metriche Chiave</h2>
          <div className="metrics-container">
            <div className="metric-item">
              <span className="metric-label">Auto Vendute:</span>
              <span className="metric-value">{metricsData.sold} su {metricsData.total} disponibili</span>
            </div>
            <div className="metric-item">
              <span className="metric-label">Auto Rimaste in Stock:</span>
              <span className="metric-value">{metricsData.remaining}</span>
            </div>
            {metricsData.brands && metricsData.brands.length > 0 && (
              <div className="metric-item">
                <span className="metric-label">Brand Più Venduti:</span>
                <span className="metric-value">
                  {metricsData.brands.slice(0, 3).map(b => `${b.brand} (${b.count})`).join(', ')}
                </span>
              </div>
            )}
            {metricsData.models && metricsData.models.length > 0 && (
              <div className="metric-item">
                <span className="metric-label">Modelli Top:</span>
                <span className="metric-value">
                  {metricsData.models.slice(0, 5).map(m => `${m.model} (${m.count})`).join(', ')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Box>
  );
};

export default SalesReportGenerator; 